/**
 * ReminderScheduleAdmin — WHICH reminders exist, and when (#167).
 *
 * Pete, 2026-08-21: *"Shouldn't these specific type of reminders have their own
 * schedule, by facility, by company, etc?"*
 *
 * ⛔ NOT THE AUTOMATED TEXTS SCREEN, and the split is the point. That one decides
 * WHETHER we may text without being asked, and ships with everything off. This one
 * decides WHEN and WHAT — a list of entries, each with its own lead time, direction
 * filter and wording. Nothing here sends anything until that screen is switched on.
 *
 * ⭐ THE HARD PART IS NOT THE FORM, IT IS MAKING INHERITANCE VISIBLE. Facility
 * entries REPLACE company entries (Pete's call: predictable, one list to read). But
 * that means adding a company-wide reminder silently does nothing at a customised
 * site — so the company entries are SHOWN here, greyed and labelled, rather than
 * hidden. An administrator who cannot see what he is overriding cannot understand
 * why his change had no effect.
 */
import { useCallback, useEffect, useState } from 'react';
import {
    AlertCircle, AlertTriangle, CheckCircle2, Info, Loader2, Plus, Save, Trash2, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
    ApiCallError, getReminderSchedule, saveReminderSchedule, deleteReminderSchedule,
    type ReminderScheduleResponseDto, type ReminderScheduleRowDto,
} from '@/api/client';

interface Props {
    getToken: () => Promise<string>;
    facilities: { FacilityCode: string; DisplayName: string }[];
}

interface DraftEntry {
    reminderScheduleId: number | null;
    scopeType: 'Company' | 'Facility';
    scopeKey: string;
    label: string;
    offsetHours: string;
    direction: string;      // '' = both
    messageTemplate: string;
    isActive: boolean;
}

const BLANK = (scopeKey: string): DraftEntry => ({
    reminderScheduleId: null,
    scopeType: 'Facility',
    scopeKey,
    label: '',
    // ⚠️ 24 rather than empty: a lead time is the one field with an obvious
    // default, and an empty box invites 0 — which is not storable and could never
    // fire (#165).
    offsetHours: '24',
    direction: '',
    messageTemplate: '',
    isActive: true,
});

/** How a driver would read the timing, so the list is scannable. */
function humanOffset(hours: number): string {
    if (hours === 24) return 'the day before';
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ahead`;
    const days = Math.round(hours / 24);
    return `about ${days} day${days === 1 ? '' : 's'} ahead`;
}

export function ReminderScheduleAdmin({ getToken, facilities }: Props) {
    const [facilityCode, setFacilityCode] = useState(facilities[0]?.FacilityCode ?? '');
    const [data, setData] = useState<ReminderScheduleResponseDto | null>(null);
    const [draft, setDraft] = useState<DraftEntry | null>(null);

    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [note, setNote] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!facilityCode) return;
        setLoading(true); setError(null);
        try {
            setData(await getReminderSchedule(await getToken(), facilityCode));
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : String(err));
        } finally {
            setLoading(false);
        }
    }, [facilityCode, getToken]);

    useEffect(() => { void load(); }, [load]);

    async function save() {
        if (!draft) return;
        const hours = Number(draft.offsetHours);
        // ⛔ CHECKED HERE TOO, not only server-side. The server refuses 0 and the
        // database refuses 0, but a form that lets somebody type it and then reports
        // a validation error from two layers down reads as a bug in the app.
        if (!Number.isInteger(hours) || hours < 1 || hours > 168) {
            setError('Hours ahead must be a whole number from 1 to 168. '
                   + 'Zero cannot work: a reminder due at the appointment time has no '
                   + 'window to fire in.');
            return;
        }
        if (!draft.label.trim()) { setError('Give the entry a name — it appears in the log.'); return; }

        setBusy(true); setError(null); setNote(null);
        try {
            const r = await saveReminderSchedule(await getToken(), {
                reminderScheduleId: draft.reminderScheduleId,
                scopeType: draft.scopeType,
                scopeKey: draft.scopeType === 'Facility' ? facilityCode : draft.scopeKey,
                label: draft.label.trim(),
                offsetHours: hours,
                direction: draft.direction || null,
                messageTemplate: draft.messageTemplate.trim() || null,
                isActive: draft.isActive,
            });
            setDraft(null);
            // ⛔ AFTER load(), NOT BEFORE — load() clears the note, so setting it first
            // wipes the confirmation before anybody reads it. Exactly the bug the
            // Automated Texts screen had (2026-08-21).
            await load();
            setNote(r.message);
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : String(err));
        } finally {
            setBusy(false);
        }
    }

    async function remove(row: ReminderScheduleRowDto) {
        setBusy(true); setError(null); setNote(null);
        try {
            await deleteReminderSchedule(await getToken(), row.reminderScheduleId);
            await load();
            setNote(`"${row.label}" removed.`);
        } catch (err) {
            // ⭐ The 409 explains itself and offers the alternative (switch it off),
            // so it is shown verbatim rather than replaced with "could not delete".
            setError(err instanceof ApiCallError ? err.details.error : String(err));
        } finally {
            setBusy(false);
        }
    }

    const own = (data?.rows ?? []).filter(r => r.scopeType === 'Facility');
    const inherited = (data?.rows ?? []).filter(r => r.scopeType === 'Company');

    return (
        <div className="space-y-4">
            <div>
                <h3 className="text-sm font-medium">Reminder schedule</h3>
                <p className="text-xs text-muted-foreground">
                    When booked drivers are reminded, ahead of their appointment. Whether they
                    are reminded at all lives on the <span className="font-medium">Automated
                    Texts</span> screen — nothing here sends anything until that is switched on.
                </p>
            </div>

            <div className="flex items-end gap-3">
                <div className="space-y-1">
                    <Label>Facility</Label>
                    <Select value={facilityCode} onValueChange={setFacilityCode}>
                        <SelectTrigger className="w-[320px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {facilities.map(f => (
                                <SelectItem key={f.FacilityCode} value={f.FacilityCode}>
                                    {f.DisplayName}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                {loading && <Loader2 className="mb-2 h-4 w-4 animate-spin text-muted-foreground" />}
            </div>

            {/* ⛔ SAID PLAINLY. "No entries" and "the table does not exist" look
                identical otherwise, and only one is fixable from this screen. */}
            {data && !data.migrationApplied && (
                <div className="flex gap-2 rounded border border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{data.message}</span>
                </div>
            )}

            {error && (
                <div className="flex gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{error}</span>
                </div>
            )}
            {note && (
                <div className="flex gap-2 rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{note}</span>
                </div>
            )}

            {/* ⭐ THE REPLACE RULE AND ITS COST, FROM THE SERVER, EVERY TIME. */}
            {data?.migrationApplied && data.overrideNote && (
                <div className="flex gap-2 rounded border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                    <Info className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{data.overrideNote}</span>
                </div>
            )}

            {data?.migrationApplied && (
                <>
                    <EntryList
                        title="This facility"
                        rows={own}
                        emptyText="No entries of its own — it uses the company schedule below."
                        onEdit={(r) => setDraft({
                            reminderScheduleId: r.reminderScheduleId,
                            scopeType: r.scopeType, scopeKey: r.scopeKey,
                            label: r.label, offsetHours: String(r.offsetHours),
                            direction: r.direction ?? '',
                            messageTemplate: r.messageTemplate ?? '',
                            isActive: r.isActive,
                        })}
                        onRemove={remove}
                        busy={busy}
                    />

                    {inherited.length > 0 && (
                        <EntryList
                            title={`Company schedule${data.facilityOverridesCompany ? ' — NOT in force here' : ''}`}
                            rows={inherited}
                            emptyText=""
                            /* ⚠️ Company rows are read-only from a facility screen. Editing
                               one here would change every OTHER site that inherits it, from
                               a page titled with one facility's name. */
                            onEdit={null}
                            onRemove={null}
                            busy={busy}
                        />
                    )}

                    {!draft && (
                        <Button variant="outline" size="sm" onClick={() => setDraft(BLANK(facilityCode))}>
                            <Plus className="mr-1 h-3.5 w-3.5" /> Add a reminder
                        </Button>
                    )}
                </>
            )}

            {draft && (
                <div className="space-y-3 rounded border p-3">
                    <div className="flex items-center justify-between">
                        <div className="text-sm font-medium">
                            {draft.reminderScheduleId ? 'Edit reminder' : 'New reminder'}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => { setDraft(null); setError(null); }}>
                            <X className="h-3.5 w-3.5" />
                        </Button>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                            <Label htmlFor="rs-label">Name</Label>
                            <Input id="rs-label" value={draft.label} placeholder="Day before"
                                onChange={(e) => setDraft({ ...draft, label: e.target.value })} />
                            <div className="text-[11px] text-muted-foreground">
                                Appears in the appointment log, so two reminders are tellable apart.
                            </div>
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="rs-hours">Hours ahead</Label>
                            <Input id="rs-hours" inputMode="numeric" value={draft.offsetHours}
                                onChange={(e) => setDraft({ ...draft, offsetHours: e.target.value })} />
                            {/* ⛔ #165 said out loud, so nobody types 0 and waits. */}
                            <div className="text-[11px] text-muted-foreground">
                                1 to 168. Zero is not allowed — a reminder due at the appointment
                                time has no window to fire in.
                            </div>
                        </div>
                        <div className="space-y-1">
                            <Label>Applies to</Label>
                            <Select value={draft.direction || 'BOTH'}
                                onValueChange={(v) => setDraft({ ...draft, direction: v === 'BOTH' ? '' : v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="BOTH">Inbound and outbound</SelectItem>
                                    <SelectItem value="INBOUND">Inbound only</SelectItem>
                                    <SelectItem value="OUTBOUND">Outbound only</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="space-y-1">
                        <Label htmlFor="rs-template">Wording</Label>
                        <Input id="rs-template" className="font-mono text-xs"
                            value={draft.messageTemplate}
                            placeholder={data?.shippedWording ?? ''}
                            onChange={(e) => setDraft({ ...draft, messageTemplate: e.target.value })} />
                        {/* ⚠️ Empty means INHERIT, not "send nothing" — and saying so is what
                            stops somebody pasting the shipped sentence in to "be safe",
                            which would freeze this entry on today's wording (#149). */}
                        <div className="text-[11px] text-muted-foreground">
                            Leave blank to use the facility&rsquo;s reminder wording. Fill it in only
                            when this entry should read differently — a two-hour reminder and a
                            day-before one should not say the same thing. Tokens:{' '}
                            <code className="font-mono">{'{facility}'}</code>{' '}
                            <code className="font-mono">{'{when}'}</code>{' '}
                            <code className="font-mono">{'{appointment}'}</code>{' '}
                            <code className="font-mono">{'{name}'}</code>
                        </div>
                        {/* ⛔ The trap worth naming at the point of typing. */}
                        <div className="text-[11px] text-amber-700">
                            Avoid &ldquo;tomorrow&rdquo; — the lead time above can be anything from an
                            hour to a week, so only {'{when}'} is always true. And it carries no
                            door: hours ahead, the door will likely change.
                        </div>
                    </div>

                    <label className="flex items-center gap-2 text-[11px] text-muted-foreground">
                        <input type="checkbox" checked={draft.isActive}
                            onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })} />
                        Active
                    </label>

                    <div className="flex justify-end">
                        <Button size="sm" onClick={save} disabled={busy}>
                            {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                  : <Save className="mr-1 h-3.5 w-3.5" />}
                            Save
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

function EntryList({ title, rows, emptyText, onEdit, onRemove, busy }: {
    title: string;
    rows: ReminderScheduleRowDto[];
    emptyText: string;
    onEdit: ((r: ReminderScheduleRowDto) => void) | null;
    onRemove: ((r: ReminderScheduleRowDto) => void) | null;
    busy: boolean;
}) {
    return (
        <div className="space-y-1">
            <div className="text-xs font-medium text-muted-foreground">{title}</div>
            {rows.length === 0 && emptyText && (
                <div className="text-[11px] text-muted-foreground">{emptyText}</div>
            )}
            {rows.map(r => (
                <div key={r.reminderScheduleId}
                    /* ⭐ An overridden or inactive row is DIMMED, not hidden. Hiding it
                       is why somebody adds a company reminder and cannot work out why
                       nothing happened. */
                    className={`rounded border px-3 py-2 ${
                        r.overriddenByFacility || !r.isActive ? 'opacity-55' : ''}`}>
                    <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                            <div className="text-sm">
                                <span className="font-medium">{r.label}</span>
                                <span className="text-muted-foreground">
                                    {' '}— {humanOffset(r.offsetHours)}
                                    {r.direction ? `, ${r.direction.toLowerCase()} only` : ''}
                                </span>
                                {!r.isActive && (
                                    <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px]">off</span>
                                )}
                                {r.overriddenByFacility && (
                                    <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-900">
                                        overridden
                                    </span>
                                )}
                            </div>
                            {/* ⭐ #164 — THE BLAST RADIUS, BESIDE THE THING THAT CAUSES IT.
                                Arming a reminder texts everybody already inside its lead
                                time on the very next sweep, because none of them has a
                                sent-row yet. Nobody should have to switch it on to find
                                out how many people that is. */}
                            {r.wouldTextNow !== null && (
                                <div className={`mt-0.5 text-[11px] ${
                                    r.wouldTextNow > 0 ? 'text-amber-700' : 'text-muted-foreground'}`}>
                                    {r.wouldTextNow === 0
                                        ? 'Nothing due right now.'
                                        : `⚠️ ${r.wouldTextNow} driver${r.wouldTextNow === 1 ? '' : 's'} `
                                          + `would be texted on the next sweep.`}
                                </div>
                            )}
                            {/* ⭐ THE MESSAGE ITSELF. A schedule row is unreadable as
                                configuration — an offset and a template are not a message,
                                and this is the only place anybody sees what lands on a
                                phone before a driver does. */}
                            <pre className="mt-1 whitespace-pre-wrap rounded bg-muted/50 px-2 py-1 font-sans text-[11px]">
{r.preview.message}
                            </pre>
                            {/**
                              * ⛔ REWORDED WITH THE SEND DIALOG (2026-08-27). It said
                              * *"nobody will notice it arriving in pieces"*, which described
                              * the rare case as the normal one — a long SMS is reassembled by
                              * the handset and shows as ONE message. ⭐ The point that
                              * survives is the one that is true every time and is worse here
                              * than in the send dialog: this template is sent AUTOMATICALLY,
                              * so it is charged twice on every reminder, forever, with nobody
                              * watching it happen.
                              */}
                            {r.preview.segments > 1 && (
                                <div className="text-[11px] text-amber-700">
                                    Charged as {r.preview.segments} text messages — and this one goes
                                    out automatically, so it costs double on every reminder.
                                </div>
                            )}
                            {r.preview.unicode && (
                                <div className="text-[11px] text-amber-700">
                                    A special character (often a curly quote pasted from Word) has
                                    halved the length limit for this message.
                                </div>
                            )}
                        </div>
                        <div className="flex shrink-0 gap-1">
                            {onEdit && (
                                <Button variant="ghost" size="sm" disabled={busy}
                                    onClick={() => onEdit(r)}>Edit</Button>
                            )}
                            {onRemove && (
                                <Button variant="ghost" size="sm" disabled={busy}
                                    onClick={() => onRemove(r)}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

export default ReminderScheduleAdmin;
